import { JsonataTransformerClass } from '@zaoblako/shared/common/classes/jsonata-transformer.class';
import { JSONATA_UTIL_BINDINGS } from '@zaoblako/shared/common/utils/jsonata-util-bindings';

import { Injectable } from '@angular/core';
import { from, type Observable } from 'rxjs';

/** Provides "JSONata" transformation with memoization */
@Injectable({ providedIn: 'root' })
export class JsonataService {
  /** "JSONata" transformers compiler instance */
  private readonly jsonataTransformer = JsonataTransformerClass.getInstance();

  /** Cached transformation results */
  private readonly cachedResults = new WeakMap<object, Map<string, any>>();

  /**
   * Transform source object to target object using received "JSONata" expression
   *
   * @template [Source] Source object type (`any` by default)
   * @template [Target] Target object type (`any` by default)
   *
   * @param source Source object
   * @param expression "JSONata" transform expression
   *
   * @returns `Promise` with target object
   */
  public async transform<Source = any, Target = any>(source: Source, expression: string): Promise<Target> {
    if (!source || !expression) {
      return null;
    }

    if (typeof source !== 'object') {
      const transformer = this.jsonataTransformer.getTransformer(expression);
      return transformer.evaluate(source, JSONATA_UTIL_BINDINGS);
    }

    let objectCache = this.cachedResults.get(source);
    if (!objectCache) {
      objectCache = new Map<string, any>();
      this.cachedResults.set(source, objectCache)
    }

    if (objectCache.has(expression)) {
      return objectCache.get(expression);
    }

    const transformer = this.jsonataTransformer.getTransformer(expression);
    const result = transformer.evaluate(source, JSONATA_UTIL_BINDINGS);
    objectCache.set(expression, result);

    return result;
  }

  /**
   * Transform source object to target object using received "JSONata" expression
   *
   * @template [Source] Source object type (`any` by default)
   * @template [Target] Target object type (`any` by default)
   *
   * @param source Source object
   * @param expression "JSONata" transform expression
   *
   * @returns `Observale` with target object
   */
  public transformAsObservable<Source = any, Target = any>(source: Source, expression: string): Observable<Target> {
    return from(this.transform(source, expression));
  }
}
